import React, { useState } from "react";
import { Plus, X, Save, Smartphone, Hash, HardDrive, Cpu } from "lucide-react";
import { AddDeviceRequest } from "../../types/api";
import { adminAddDeviceToUser } from "./userApi";
import styles from "./UserShared.module.css";

interface AddDeviceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onDeviceAdded: () => void;
  userId: string;
}

const AddDeviceModal: React.FC<AddDeviceModalProps> = ({
  isOpen,
  onClose,
  onDeviceAdded,
  userId,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  // Form fields
  const [model, setModel] = useState("");
  const [hardwareVersion, setHardwareVersion] = useState("");
  const [firmwareVersion, setFirmwareVersion] = useState("");
  const [serialNumber, setSerialNumber] = useState("");
  const [isActive, setIsActive] = useState(true);

  const resetForm = () => {
    setModel("");
    setHardwareVersion("");
    setFirmwareVersion("");
    setSerialNumber("");
    setIsActive(true);
    setError("");
    setSuccess("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!model.trim() || !serialNumber.trim()) {
      setError("Model ve seri numarası gereklidir");
      return;
    }

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const deviceData: AddDeviceRequest = {
        model: model.trim(),
        serial_number: serialNumber.trim(),
        is_active: isActive,
      };

      if (hardwareVersion.trim()) {
        deviceData.hardware_version = hardwareVersion.trim();
      }

      if (firmwareVersion.trim()) {
        deviceData.firmware_version = firmwareVersion.trim();
      }

      // Admin endpoint'ini kullan - kullanıcı ID'si ile birlikte
      await adminAddDeviceToUser(userId, deviceData);

      setSuccess("Cihaz başarıyla kullanıcıya eklendi!");

      // 1.5 saniye bekleyip modal'ı kapat
      setTimeout(() => {
        onDeviceAdded();
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error("Device add error:", err);

      // API hata mesajlarını daha anlaşılır hale getir
      let errorMessage = "Cihaz eklenirken hata oluştu";

      if (err.response?.status === 400) {
        errorMessage =
          "Geçersiz cihaz bilgileri. Lütfen tüm alanları kontrol edin.";
      } else if (err.response?.status === 403) {
        errorMessage = "Bu işlemi gerçekleştirmek için yetkiniz yok.";
      } else if (err.response?.status === 404) {
        errorMessage = "Kullanıcı bulunamadı.";
      } else if (err.response?.status === 409) {
        errorMessage = "Bu seri numarasına sahip cihaz zaten mevcut.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={`${styles.modal} ${styles.addDeviceModal}`}>
        <div className={styles.modalHeader}>
          <h2>
            <Plus className={styles.modalIcon} />
            Yeni Cihaz Ekle
          </h2>
          <button
            onClick={handleClose}
            className={styles.modalCloseButton}
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalContent}>
          {error && (
            <div className={styles.errorMessage}>
              <div className={styles.errorIcon}>
                <span role="img" aria-label="Warning">
                  ⚠️
                </span>
              </div>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className={styles.successMessage}>
              <div className={styles.successIcon}>
                <span role="img" aria-label="Success">
                  ✅
                </span>
              </div>
              <span>{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className={styles.addDeviceForm}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="model" className={styles.formLabel}>
                  <Smartphone className={styles.labelIcon} />
                  Model *
                </label>
                <input
                  type="text"
                  id="model"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder="Örn: DrumPad Pro"
                  className={styles.formInput}
                  disabled={loading}
                  required
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="serialNumber" className={styles.formLabel}>
                  <Hash className={styles.labelIcon} />
                  Seri Numarası *
                </label>
                <input
                  type="text"
                  id="serialNumber"
                  value={serialNumber}
                  onChange={(e) => setSerialNumber(e.target.value)}
                  placeholder="Örn: DP2024001234"
                  className={styles.formInput}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label htmlFor="hardwareVersion" className={styles.formLabel}>
                  <HardDrive className={styles.labelIcon} />
                  Hardware Versiyon
                </label>
                <input
                  type="text"
                  id="hardwareVersion"
                  value={hardwareVersion}
                  onChange={(e) => setHardwareVersion(e.target.value)}
                  placeholder="Örn: v2.1"
                  className={styles.formInput}
                  disabled={loading}
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="firmwareVersion" className={styles.formLabel}>
                  <Cpu className={styles.labelIcon} />
                  Firmware Versiyon
                </label>
                <input
                  type="text"
                  id="firmwareVersion"
                  value={firmwareVersion}
                  onChange={(e) => setFirmwareVersion(e.target.value)}
                  placeholder="Örn: 1.4.2"
                  className={styles.formInput}
                  disabled={loading}
                />
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className={styles.checkbox}
                  disabled={loading}
                />
                <span className={styles.checkboxText}>Cihaz aktif</span>
              </label>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                onClick={handleClose}
                className={styles.cancelButton}
                disabled={loading}
              >
                İptal
              </button>
              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading || !model.trim() || !serialNumber.trim()}
              >
                {loading ? (
                  "Ekleniyor..."
                ) : (
                  <>
                    <Save size={16} />
                    Cihaz Ekle
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddDeviceModal;
